import { sendWarning } from '../plugins/sentry';
import Icons from './icons';
import { toast } from './toast';

const POLL_INTERVAL = 5 * 60 * 1000;
const STALE_TAB_THRESHOLD = 4 * 60 * 60 * 1000;
const PRELOAD_MAX_RETRIES = 3;
const PRELOAD_RETRY_WINDOW = 60 * 1000;
const PRELOAD_RETRY_KEY = 'vite_preload_error_retry';

// Set once an update has been handled (silently activated or offered via
// toast), so a fast second deploy or a repeated updatefound doesn't show a
// second toast / trigger a second reload.
let updateDetected = false;

// Set right before registration.update() is called by the polling loop.
// Distinguishes "we went looking for an update" (polling -> ask the user)
// from "the browser found one on its own" (updatefound -> handle silently).
let explicitUpdateRequested = false;

// Set once setupStaleChunkReload() has started a reload, so further failing
// chunks before the page unloads don't count as new attempts.
let staleChunkReloading = false;

function reloadOnce() {
  let reloaded = false;
  return () => {
    if (reloaded) return;
    reloaded = true;
    window.location.reload();
  };
}

/**
 * Activate a waiting worker without asking the user, then reload once it's
 * safe. Used when an update was found passively (not via polling) - e.g. a
 * deploy that happened while the tab was idle or between page loads.
 */
function activateSilently(registration: ServiceWorkerRegistration) {
  const waiting = registration.waiting;
  if (!waiting) return;
  if (!waiting.scriptURL.includes('/sw.js')) return;
  if (updateDetected) return;

  updateDetected = true;
  toast('Oppdaterer til ny versjon av Timer...', { icon: Icons.RotateRight });

  const doReload = reloadOnce();

  // Primary signal: the waiting worker reaching 'activated'. This can fail
  // to fire if the worker gets orphaned from registration.waiting once it
  // moves to 'activating' - the browser freezes its .state at 'installed'
  // and stops sending it statechange events. controllerchange below is the
  // reliable fallback for that case.
  waiting.addEventListener('statechange', () => {
    if (waiting.state === 'activated') doReload();
  });

  // Guard with a flag we set ourselves, not waiting.state - see above, it's
  // frozen at 'installed' once orphaned so it can't be used as a guard here.
  let skipWaitingSent = false;
  const onControllerChange = () => {
    if (skipWaitingSent) {
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
      doReload();
    }
  };
  navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);

  skipWaitingSent = true;
  waiting.postMessage({ type: 'SKIP_WAITING' });
}

/**
 * Offer the update to the user instead of activating it automatically. Used
 * when the update was found because we explicitly polled for one - the user
 * may be mid-task, so we let them choose when to reload.
 */
function showUpdateToast(registration: ServiceWorkerRegistration) {
  const waiting = registration.waiting;
  if (waiting && !waiting.scriptURL.includes('/sw.js')) return;
  if (updateDetected) return;
  updateDetected = true;

  toast('Ny versjon av Timer er tilgjengelig', {
    persistent: true,
    icon: Icons.RotateRight,
    actions: [
      {
        label: 'Oppdater',
        onClick: (toastInstance) => {
          toastInstance.close();

          if (registration.waiting) {
            registration.waiting.postMessage({ type: 'SKIP_WAITING' });
            navigator.serviceWorker.addEventListener('controllerchange', reloadOnce(), {
              once: true,
            });
          } else {
            window.location.reload();
          }
        },
      },
      {
        label: 'Senere',
        onClick: (toastInstance) => toastInstance.close(),
      },
    ],
  });
}

/**
 * Register the service worker and start watching for new versions: both
 * passively (updatefound, fired whenever the browser's own periodic check
 * finds a new sw.js) and actively (polling every 5 minutes and whenever the
 * tab becomes visible again).
 */
export function setupServiceWorkerUpdates() {
  if (!('serviceWorker' in navigator)) return;

  navigator.serviceWorker.register('/sw.js').then((registration) => {
    registration.addEventListener('updatefound', () => {
      const worker = registration.installing;

      worker?.addEventListener('statechange', () => {
        if (worker.state !== 'installed' || !registration.active) return;

        if (updateDetected) {
          // A second deploy landed while the first update was still being
          // activated - just push it through, the page is already handling one.
          worker.postMessage({ type: 'SKIP_WAITING' });
        } else if (explicitUpdateRequested) {
          showUpdateToast(registration);
        } else {
          navigator.serviceWorker.getRegistration().then((reg) => {
            if (reg?.waiting) activateSilently(reg);
          });
        }
      });
    });

    const checkForUpdate = () => {
      if (updateDetected) return;
      explicitUpdateRequested = true; // must be set before update() is called
      registration.update().catch(() => {});
    };

    setInterval(checkForUpdate, POLL_INTERVAL);
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) checkForUpdate();
    });
  });
}

/**
 * Call once the app has mounted: picks up a worker that was already waiting
 * from a deploy that happened while this page was loading (or after a
 * previous "Senere").
 */
export function notifyServiceWorkerMounted() {
  if (!('serviceWorker' in navigator)) return;

  navigator.serviceWorker.getRegistration().then((registration) => {
    // registration.active, not navigator.serviceWorker.controller - the
    // controller is briefly null right as a new worker starts controlling
    // the page, which would make us skip activating a waiting worker in
    // that window.
    if (registration?.waiting && registration?.active) {
      activateSilently(registration);
    }
  });
}

/**
 * Reload a tab that's been hidden for a long time once the user comes back
 * to it, so it doesn't resume on a version of the app that's hours stale.
 * Standalone - shares no state with setupServiceWorkerUpdates(), call it
 * once at startup alongside it.
 */
export function setupStaleTabReload() {
  let hiddenAt: number | null = null;

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      hiddenAt = Date.now();
      return;
    }

    if (!hiddenAt) return;
    const hiddenFor = Date.now() - hiddenAt;
    hiddenAt = null;

    if (hiddenFor < STALE_TAB_THRESHOLD) return;
    if (!navigator.onLine) return;

    reloadWithWaitingWorker();
  });
}

/**
 * Reload a tab whose lazy-loaded chunk no longer exists because a deploy
 * replaced it (Vite fires vite:preloadError). Without this the navigation
 * just fails and the tab stays broken on the old version. Capped per session
 * so a chunk that is genuinely missing can't cause a reload loop.
 */
export function setupStaleChunkReload() {
  window.addEventListener('vite:preloadError', (event) => {
    const payload = (event as Event & { payload?: unknown }).payload;

    // Tag the error so beforeSend in plugins/sentry.ts drops it - wording
    // varies per browser, so matching on message misses Safari and Firefox.
    // Vite still re-throws it, which Vue Router handles.
    if (payload instanceof Error) {
      (payload as Error & { isStaleChunkError?: true }).isStaleChunkError = true;
    }

    if (staleChunkReloading) return;

    // Count attempts within a time window rather than resetting on 'load':
    // the failing import can run after 'load', which would reset the counter
    // on every reload and never stop the loop.
    const now = Date.now();
    let retry = { count: 0, at: 0 };
    try {
      retry = JSON.parse(sessionStorage.getItem(PRELOAD_RETRY_KEY) ?? '') ?? retry;
    } catch {
      // Missing or malformed - start fresh
    }
    const retries = now - retry.at < PRELOAD_RETRY_WINDOW ? retry.count : 0;
    const chunk = payload instanceof Error ? payload.message : String(payload);

    if (retries >= PRELOAD_MAX_RETRIES) {
      sendWarning('Stale chunk reload gave up', { chunk, attempt: String(retries) });
      alert('Applikasjonen kunne ikke oppdatere. Vennligst prøv å laste siden på nytt manuelt.');
      return;
    }

    try {
      sessionStorage.setItem(PRELOAD_RETRY_KEY, JSON.stringify({ count: retries + 1, at: now }));
    } catch {
      // Storage unavailable - can't track attempts, so don't risk a loop
      return;
    }

    // A route's JS and CSS chunks can both fail - only the first one reloads.
    staleChunkReloading = true;
    reloadWithWaitingWorker();
  });
}

/**
 * Reload, but first activate a waiting worker and wait for controllerchange,
 * so the fresh page loads under the new SW's control instead of triggering
 * the update toast again right after.
 */
function reloadWithWaitingWorker() {
  const doReload = () => window.location.reload();

  if (!('serviceWorker' in navigator)) {
    doReload();
    return;
  }

  navigator.serviceWorker
    .getRegistration()
    .then((registration) => {
      if (registration?.waiting) {
        navigator.serviceWorker.addEventListener('controllerchange', doReload, { once: true });
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      } else {
        doReload();
      }
    })
    .catch(doReload);
}
